// ============================================================
// TrialRequestsPage.js
// Global list of all trial requests, newest first. Status filter
// + click-through to the detail/provisioning page.
//
// URL state: ?page=N, ?pageSize=N, ?status=...
//
// Mirrors ClientsPage structure (DataTable + toolbar + URL-driven
// state). Reuses TenantsPage.css / ClientsPage.css classes.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { TrialStatusBadge } from '../components/StatusBadge';
import { listAdminTrialRequests } from '../api/adminTrialRequests';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './TenantsPage.css';
import './ClientsPage.css';

const DEFAULT_PAGE_SIZE = 50;
const STATUS_OPTIONS = ['New', 'Provisioned', 'Extended', 'Converted', 'Declined'];
const ACTIVE_TRIAL_STATUSES = new Set(['Provisioned', 'Extended']);

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

// Whole days from now until the expiry date (rounded up). Returns
// null when there's no expiry (not yet provisioned).
function daysRemaining(trialExpiryDate) {
  if (!trialExpiryDate) return null;
  const expiry = new Date(trialExpiryDate);
  if (Number.isNaN(expiry.getTime())) return null;
  return Math.ceil((expiry.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

export default function TrialRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePageParam(searchParams.get('page'), 1);
  const pageSize = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const urlStatus = searchParams.get('status') || '';

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (urlStatus) params.status = urlStatus;
      const res = await listAdminTrialRequests(params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, urlStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleStatusChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('status', value); else next.delete('status');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      mobile: 'subtitle',
      render: (row) => (
        <span className="tenants-page__name">{`${row.firstName} ${row.lastName}`.trim()}</span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      mobile: 'meta',
      render: (row) => <span className="tenants-page__muted">{row.email}</span>,
      width: '220px',
    },
    {
      key: 'organisation',
      label: 'Organisation',
      mobile: 'title',
      render: (row) => <span className="tenants-page__name">{row.organisationName}</span>,
      width: '200px',
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => <span className="tenants-page__muted">{row.role}</span>,
      width: '150px',
    },
    {
      // Slice C: lead-level attribution. utm_content is the most
      // specific discriminator (seg<X>-email<N>); fall back to
      // source, dash when uncaptured (direct visit / pre-migration).
      key: 'source',
      label: 'Source',
      // Marketing attribution, and a dash on most rows. Kept on the
      // desktop table where it costs nothing; dropped from the phone
      // card so the triage-relevant fields lead.
      mobile: 'hide',
      render: (row) => (
        <span className="tenants-page__muted" title={[row.utmSource, row.utmMedium, row.utmCampaign, row.utmContent].filter(Boolean).join(' · ') || undefined}>
          {row.utmContent || row.utmSource || '—'}
        </span>
      ),
      width: '130px',
    },
    {
      key: 'status',
      label: 'Status',
      mobile: 'badge',
      render: (row) => <TrialStatusBadge status={row.status} />,
      width: '120px',
    },
    {
      key: 'submitted',
      label: 'Submitted',
      render: (row) => <span className="tenants-page__muted">{formatDate(row.submittedAt)}</span>,
      width: '130px',
    },
    {
      key: 'expiry',
      label: 'Trial Expiry',
      mobileLabel: 'Expires',
      render: (row) => (
        <span className="tenants-page__muted">
          {row.trialAwaitingFirstSignIn
            ? 'Waiting for first sign-in'
            : (row.trialExpiryDate ? formatDate(row.trialExpiryDate) : '—')}
        </span>
      ),
      width: '130px',
    },
    {
      key: 'daysRemaining',
      label: 'Days Remaining',
      mobileLabel: 'Remaining',
      render: (row) => {
        const days = daysRemaining(row.trialExpiryDate);
        // A waiting trial's date is the 90-day waiting deadline, not its end.
        if (days == null || row.trialAwaitingFirstSignIn || !ACTIVE_TRIAL_STATUSES.has(row.status)) {
          return <span className="tenants-page__muted">—</span>;
        }
        const isUrgent = days <= 7;
        return (
          <span style={isUrgent ? { color: 'var(--color-error)', fontWeight: 600 } : undefined}>
            {days} {days === 1 ? 'day' : 'days'}
          </span>
        );
      },
      width: '130px',
      align: 'right',
    },
  ];

  function handleRowClick(row) {
    navigate(`/trial-requests/${row.trialRequestId}`);
  }

  const isFiltered = Boolean(urlStatus);

  return (
    <>
      <PageHeader
        title="Trial Requests"
        subtitle="Free-trial sign-ups from a2zcarehub.co.uk."
      />

      <div className="clients-page__toolbar">
        <div className="clients-page__filters">
          <label className="clients-page__tenant-filter">
            <span className="clients-page__tenant-label">Status</span>
            <select
              value={urlStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="clients-page__tenant-select"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.trialRequestId}
        onRowClick={handleRowClick}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={isFiltered ? 'No trial requests match this status.' : 'No trial requests yet.'}
      />
    </>
  );
}
